<template>
  <layout v-show="page != 'empty'" :tenantId="tenantId">

    <main v-if="isSignFlowNotFound" :aria-label="t('SignFlowNotFoundErrorMessage')">
      <h1 class="error">{{ t('SignFlowNotFoundErrorMessage') }}</h1>
    </main>

    <main v-else-if="isSignFlowCancelled" :aria-label="t('SignFlowCanceledErrorMessage')">
      <h1 class="error">{{ t('SignFlowCanceledErrorMessage') }}</h1>
      <p class="error">{{ signFlowCancellationComment }}</p>
    </main>

    <main v-else-if="isSignFlowRejectedBySigner" :aria-label="t('SignFlowRejectedBySignerErrorMessage')">
      <h1 class="error">{{ t('SignFlowRejectedBySignerErrorMessage') }}</h1>
    </main>

    <template v-else-if="isSignFlowValidating" :aria-label="t('SignFlowValidatingTitle')">
      <div class="CentralMessage">
        <h1 class="sr-only">{{ t('SignFlowValidatingTitle') }}</h1>
        <p>{{ t('SignFlowValidating') }}</p>
      </div>
    </template>

    <main v-else-if="page == 'unauthorizedError'" :aria-label="t('Logout')">
      <h1>{{ t('UnauthorizedErrorHeader') }}</h1>
      <p>{{ t(unauthorizedErrorMessageIdentifier) }}</p>
      <button class="primary" v-on:click="logout" :title="t('Logout')">{{ t('Logout' ) }}</button>
    </main>

    <template v-else-if="page == 'loading' || page == 'initialLoading'">
      <main id="loading" :aria-label="t('Loading')"></main>
      <footer id="loadingFooter"></footer>
    </template>

    <main v-else-if="page == 'identityServerError'" :aria-label="t('IdentityServerErrorMessage')">
      <h1 class="error">{{ t('IdentityServerErrorMessage') }}</h1>
    </main>

    <main v-else-if="page == 'login'" :aria-label="t('Login')">
      <template>
        <h1>{{ t('LoginPrompt') }}</h1>
      </template>
      <template v-if="unauthorizedError">
        <button class="primary" v-on:click="logoutAndThenLogin">{{ t('Login') }}</button>
      </template>
      <template v-else>
        <button class="primary" v-on:click="login">{{ t('Login') }}</button>
      </template>
    </main>

    <template v-else-if="page == 'document'">

      <v-tabs v-model="currentTab" align-tabs="start" class="flex-0-0 pt-2 pl-2" hide-slider>
        <v-tab value="signing-info" v-if="showSigningInfoTab" aria-controls="signingInfoTabContents" class="border rounded-t-lg rounded-b-0 ma-0 pl-2 pr-2" density="compact" :aria-label="t('SigningInfoHeader')">
          <div class="symbol symbol-signing-info"></div>
        </v-tab>
        <v-tab value="document" aria-controls="documentTabContents" class="border rounded-t-lg rounded-b-0 ma-0">
          <div class="symbol symbol-document"></div>
          <span>{{ t('SignTabButtonText') }}</span>
        </v-tab>
        <v-tab value="attachments" aria-controls="attachmentsTabContents" class="border rounded-t-lg rounded-b-0 ma-0">
          <div class="symbol symbol-attachments"></div>
          <span>{{ t('AttachmentsTabButtonText') }}</span>
        </v-tab>
      </v-tabs>

      <!-- Not using v-tabs-window here due to incompatibility with current flex layout structure -->

      <!-- The element must be created only when visible (it's not initially visible when opening a link to an attachment, etc), or pdfobject's page fitting won't work - therefore must use v-if -->
      <!-- The element also mustn't be created more than once, or the PDF will be reloaded and scroll position etc reset - therefore must use v-show after the viewer is initially loaded -->
      <main id="documentTabContents" v-if="currentTab == 'document'" :aria-label="t('Document')">
        <h1 class="sr-only">{{ t('Document') }}</h1>
        <p class="sr-only">
          <a :href="loadedSignFileUrl" download>{{ isSignFlowFinalized ? t('DownloadFinalizedDocument') : t('DownloadUnsignedDocument') }}</a>
        </p>
        <pdfviewer aria-hidden="true" v-bind:fileUrl="loadedSignFileUrl" @viewerCreated="isDocumentViewerLoaded = true; "></pdfviewer>
      </main>
      <template v-if="currentTab == 'document'">
        <footer v-if="isAlreadySigned" :aria-label="t('AlreadySigned')" aria-live="polite">
          {{ t('AlreadySigned') }}
        </footer>
        <footer v-else-if="isSignFlowFinalized" :aria-label="t('FinalizedDocument')" aria-live="polite">
          <div>{{ t('FinalizedDocument') }}</div>
          <button v-on:click="downloadFinalizedDocument" class="primary">{{ t('DownloadFinalizedDocument') }}</button>
          <button v-if="zipFilesAfterFinalization" v-on:click="downloadFinalizedZip" class="primary">{{ t('DownloadFinalizedZip') }}</button>
        </footer>
        <footer v-else-if="isSignFlowAlreadySignedAndWaitingForFinalization" :aria-label="t('WaitingForFinalization_' + signingRole)" aria-live="polite">
          {{ t('WaitingForFinalization_' + signingRole) }}
        </footer>
        <footer v-else-if="isSignFlowAlreadySignedAndWaitingForOtherSigners" :aria-label="t('WaitingForOthers_' + signingRole)" aria-live="polite">
          {{ t('WaitingForOthers_' + signingRole) }}
        </footer>
        <footer v-else aria-live="polite">
          <template v-if="!dialog && !isViewer">
            <button v-on:click="showRejectDialog">{{ t('RejectButtonText') }}</button>
            <!-- todo: if procuration is enabled, the procuration selection must be showed when starting scs signing, so that it can be included in the cover page -->
            <button v-if="useScsForSigning && !isScsStarted" v-on:click.once="onBeginScs" key="start" class="primary">{{ t('BeginScsSigningButtonText') }}</button>
            <button v-else v-on:click="showSignDialog" class="primary">
              {{ signingRole == signingRoles.Approver ? t('ApproveButtonText') : t('SignButtonText') }}
            </button>
          </template>
        </footer>
      </template>

      <main id="attachmentsTabContents" class="attachments" v-show="currentTab == 'attachments'" :aria-label="t('Attachments')">
        <template v-if="!selectedAttachment">
          <h1>{{ t('Attachments') }}</h1>
          <div v-if="!attachments" class="loading"></div>
          <p v-else-if="!attachments || attachments.length == 0" :aria-label="t('NoAttachments')">{{ t('NoAttachments') }}</p>
          <ul v-else>
            <li v-for="attachment in attachments" v-bind:key="attachment.id">
              <a
                class="sr-only"
                :href="attachmentUri(attachment.id)"
                download>
                {{ attachment.fileName }}
              </a>
              <button 
                aria-hidden="true"
                v-on:click="attachmentOnClick(attachment)" 
                :class="{'attachment-pdf': attachmentIsPdf(attachment.contentType), attachment: true}" 
                :aria-label="t('OpenAttachment').replace('$', attachment.fileName)">
                {{ attachment.fileName }}
              </button>
            </li>
          </ul>
        </template>
        <template v-else>
          <template v-for="attachment in attachments">
            <div class="attachmentPreview" aria-hidden="true" v-bind:key="attachment.id" v-show="selectedAttachmentId == attachment.id" v-if="selectedAttachmentId == attachment.id || isAttachmentViewerLoaded[attachment.id]">
              <div class="flex-row">
                <h2 class="flex-grow">{{ t('Attachment') }}: {{attachment.fileName}}</h2>
                <button v-on:click="selectedAttachmentId = null;" class="closeButton" :aria-label="t('Close')"></button>
              </div>
              <div class="flex-row" style="height:100%;">
                <pdfviewer v-bind:fileUrl="attachment.loadedUrl" @loaded="isAttachmentViewerLoaded[attachment.id] = true"></pdfviewer>
              </div>
            </div>
          </template>
        </template>
      </main>

      <main id="signingInfoTabContents" class="signing-info" v-if="showSigningInfoTab" v-show="currentTab == 'signing-info'" :aria-label="t('SigningInfoHeader')">
        <h1>{{ t('SigningInfoHeader') }}</h1>

        <h2 v-if="invitationDetails?.length">{{ t('SigningInfoSubHeader3') }}</h2>
        <data-list
          :listData="invitationDetails"
          :styleLabelAsLabel="true">
        </data-list>

        <template v-if="!isViewer">
          <h2>{{ t('SigningInfoSubHeader1') }}</h2>
          <data-list
            :listData="signerDetailsCollected"
            :styleLabelAsLabel="true">
          </data-list>
          <p class="mt-0 pb-4"><router-link :to="{ name: 'info', params: {tenantId: tenantId}, query: {lang: locale}}"> {{ t('InfoPageUrl') }}</router-link></p>
        </template>

        <template v-if="approvers?.length">
          <h2>{{ t('SigningInfoSubHeader3Approvers') }}</h2>
          <data-list
            :listData="approvers"
            :styleLabelAsLabel="false">
          </data-list>
        </template>
        <template v-if="signers?.length">
          <h2>{{ t('SigningInfoSubHeader2Signers') }}</h2>
          <data-list
            :listData="signers"
            :styleLabelAsLabel="false">
          </data-list>
        </template>
        <template v-if="signingInfo?.viewers?.length">
          <h2>{{ t('SigningInfoSubHeader2Viewers') }}</h2>
          <data-list
            :listData="viewers"
            :styleLabelAsLabel="false">
          </data-list>
        </template>

        <div class="signinginfobuttons">
          <button v-on:click="logout">{{ t('Logout') }}</button>
          <button v-on:click="openTab('document');" class="primary">{{ t('SigningInfoOpenSignTab') }}</button>
        </div>
      </main>

    </template>


    <template v-if="page != 'initialLoading'">

      <template v-if="dialog == 'sign'">
        <!-- Tarkastaja -->
        <template v-if="signingRole == signingRoles.Approver">
          <section class="dialog" :aria-label="t('ApproveDialogHeader')">
            <div class="box">
              <div class="header">{{ t('ApproveDialogHeader') }}</div>
              <div class="main" v-if="isSigningTimedOut">
                <p>{{ t('SigningTimedOutText') }}</p>
              </div>
              <div class="main" v-else>
                <p>{{ t('ApproveDialogText1') }}</p>
                <p>{{ t('ApproveDialogText2') }}</p>
                <ul>
                  <li>{{ t('ApproveDialogField_Name') }}</li>
                  <li>{{ t('UserIdentifierType_'+loginInfo.userIdentifierType) }}</li>
                </ul>
              </div>
              <div class="buttons">
                <button v-on:click="closeDialog" key="cancelSignDialog">{{ t('ApproveDialogCancelButtonText') }}</button>
                <button v-if="isSigningTimedOut" v-on:click="logoutTimeout" class="primary">{{ t('Logout') }}</button>
                <template v-else>
                  <button v-if="useScsForSigning" v-on:click.once="onFinishScs" class="primary">{{ t('ApproveDialogFinishScsButtonText') }}</button>
                  <button v-else v-on:click.once="onSign" :disabled="procurationEnabled && !selectedProcuration" class="primary">{{ t('ApproveDialogSignButtonText') }}</button>
                </template>
              </div>
            </div>
          </section>
        </template>
        <!-- Allekirjoittaja -->
        <template v-else>
          <section class="dialog" :aria-label="t('SignDialogHeader')">
            <div class="box">
              <div class="header">{{ t('SignDialogHeader') }}</div>
              <div class="main" v-if="isSigningTimedOut">
                <p>{{ t('SigningTimedOutText') }}</p>
              </div>
              <div class="main" v-else>
                <p>{{ t('SignDialogText1') }}</p>
                <p>{{ t('SignDialogText2') }}</p>
                <ul>
                  <li>{{ t('SignDialogField_Name') }}</li>
                  <li>{{ t('UserIdentifierType_'+loginInfo.userIdentifierType) }}</li>
                </ul>
                <div v-if="procurationEnabled">
                  <p>{{ t('SignDialogField_Procuration') }}</p>
                  <select id="procuration" v-model="selectedProcuration">
                    <option :value="procs.Representative" :label="t('SignDialogField_ProcurationRepresentative')"></option>
                    <option :value="procs.Authority" :label="t('SignDialogField_ProcurationAuthority')"></option>
                    <option :value="procs.None" :label="t('SignDialogField_ProcurationOther')"></option>
                  </select>
                </div>
              </div>
              <div class="buttons">
                <button v-on:click="closeDialog" key="cancelSignDialog">{{ t('SignDialogCancelButtonText') }}</button>
                <button v-if="isSigningTimedOut" v-on:click="logoutTimeout" class="primary">{{ t('Logout') }}</button>
                <template v-else>
                  <button v-if="useScsForSigning" v-on:click.once="onFinishScs" class="primary">{{ t('SignDialogFinishScsButtonText') }}</button>
                  <button v-else v-on:click.once="onSign" :disabled="procurationEnabled && !selectedProcuration" class="primary">{{ t('SignDialogSignButtonText') }}</button>
                </template>
              </div>
            </div>
          </section>
        </template>
      </template>
      <section class="dialog" v-else-if="dialog == 'reject'" :aria-label="t('RejectDialogHeader')">
        <div class="box">
          <div class="header">{{ t('RejectDialogHeader') }}</div>
          <div class="main">
            <label>{{ t('RejectDialogRejectionComment') }} <span class="icon-required" />
              <textarea maxlength="500" id="rejectionComment" v-model="rejectionComment"></textarea>
            </label>
          </div>
          <div class="buttons">
            <button v-on:click="closeDialog" key="cancelRejectDialog">{{ t('RejectDialogCancelButtonText') }}</button>
            <button key="reject" v-on:click.once="onReject" v-bind:disabled="rejectionComment.length == 0" class="primary">{{ t('RejectDialogRejectButtonText') }}</button>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'signingInProgress'" :aria-label="t('SigningInProgressDialogHeader')">
        <div class="box">
          <div class="header">{{ t('SigningInProgressDialogHeader') }}</div>
          <div class="main">
            <p>
              <em>{{ t('SigningInProgressDialogText') }}</em>
            </p>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'signed'" :aria-label="t('SignedDialogHeader')">
        <div class="box">
          <div class="header">{{ t('SignedDialogHeader') }}</div>
          <div class="main">
            <p>
              <em>{{ t('SignedDialogText') }}</em>
            </p>
            <p>
              <button v-on:click="logout">{{ t('Logout') }}</button>
            </p>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'rejectionInProgress'" :aria-label="t('RejectionInProgressDialogHeader')">
        <div class="box">
          <div class="header">{{ t('RejectionInProgressDialogHeader') }}</div>
          <div class="main">
            <p>
              <em>{{ t('RejectionInProgressDialogText') }}</em>
            </p>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'rejected'" :aria-label="t('RejectedDialogHeader')">
        <div class="box">
          <div class="header">{{ t('RejectedDialogHeader') }}</div>
          <div class="main">
            <p>
              <em>{{ t('RejectedDialogText') }}</em>
            </p>
            <p>
              <button v-on:click="logout">{{ t('Logout') }}</button>
            </p>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'signingFailed'" :aria-label="t('SigningFailedDialogHeader')">
        <div class="box">
          <div class="header">{{ t('SigningFailedDialogHeader') }}</div>
          <div class="main">
            <p>
              <button v-on:click="logout">{{ t('Logout') }}</button>
            </p>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'rejectionFailed'" :aria-label="t('RejectionFailedDialogHeader')">
        <div class="box">
          <div class="header">{{ t('RejectionFailedDialogHeader') }}</div>
          <div class="main">
            <p>
              <button v-on:click="logout">{{ t('Logout') }}</button>
            </p>
          </div>
        </div>
      </section>
      <section class="dialog" v-else-if="dialog == 'zippingInProgress'" :aria-label="t('ZipDialogHeader')">
        <div class="box">
          <div class="header">{{ t('ZipDialogHeader') }}</div>
          <div class="main">
            <p>
              <em>{{ t('ZipDialogContent') }}</em>
            </p>
            <button v-on:click="closeDialog">{{ t('ZipDialogClose') }}</button>
          </div>
        </div>
      </section>

    </template>

  </layout>
</template>

<script src="./sign2.ts"></script>
<style src="./sign2.css"/>
